import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePostHog } from 'posthog-js/react';
import { IconArrowLeft } from '@tabler/icons-react';
import ManagePostCard from '../components/ManagePostCard';
import AutoUploadDiscoveryModal from '../components/AutoUploadDiscoveryModal';
import Footer from '../components/Footer';
import { db, storage, functions } from '../firebase';
import { doc, onSnapshot, getDoc, updateDoc } from 'firebase/firestore';
import { ref, getDownloadURL } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import './ManagePostPage.css';

const ManagePostPage = ({ user, onCreditsUpdated }) => {
  const { postId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const posthog = usePostHog();
  const [post, setPost] = useState(null);
  const [requestData, setRequestData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [hasRefreshedCredits, setHasRefreshedCredits] = useState(false);
  const [showAutoUploadDiscovery, setShowAutoUploadDiscovery] = useState(false);
  const [autoUploadEnabled, setAutoUploadEnabled] = useState(false);
  const autoUploadDiscoveryCheckedRef = useRef(false);
  const autoUploadDiscoveryTimerRef = useRef(null);
  const unsubscribeRef = useRef(null);
  const previousStatusRef = useRef(null);

  // Test mode: simulate generation progress for 10 seconds
  const isTestMode = searchParams.get('test') === '1';
  const [testSimulating, setTestSimulating] = useState(isTestMode);
  const testStartRef = useRef(isTestMode ? Date.now() : null);

  useEffect(() => {
    if (!isTestMode || !testSimulating) return;
    testStartRef.current = testStartRef.current || Date.now();
    const timer = setTimeout(() => setTestSimulating(false), 10000);
    return () => clearTimeout(timer);
  }, [isTestMode, testSimulating]);

  // Build the requestData that gets passed to ManagePostCard
  // In test mode while simulating, override status to fake generation-in-progress
  const effectiveRequestData = (isTestMode && testSimulating && requestData)
    ? {
        ...requestData,
        status: 'openai_processing',
        error: null,
        generationStartTime: new Date(testStartRef.current),
        __testDurationSeconds: 10, // progress goes 0→100% in 10 seconds
      }
    : requestData;

  // Check if this post belongs to the current user
  const isOwnPost = user && requestData && requestData.userId === user.uid;

  useEffect(() => {
    // Reset credit refresh flag and status tracking when postId changes
    setHasRefreshedCredits(false);
    previousStatusRef.current = null;
    
    // Clean up previous listener if it exists
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }
    
    setIsLoading(true);
    const docRef = doc(db, 'GenerationRequests', postId);
    
    unsubscribeRef.current = onSnapshot(docRef, async (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        setRequestData(data);
        
        // Check if user owns this post
        if (user && data.userId !== user.uid) {
          // Redirect to public view if not owner
          navigate(`/post/${postId}`);
          return;
        }
        
        // Load images - prioritize generated map, only show when it's ready
        let origUrl = null;
        let genUrl = null;
        let videoUrl = null;

        // Check if generation is still in progress
        const isGenerating = data.status !== 'openai_processing_complete' && !data.error;
        const isComplete = data.status === 'openai_processing_complete' && !data.error;

        // Load generated image FIRST - this is what users want to see
        if (data.generatedImagePath) {
          try {
            genUrl = await getDownloadURL(ref(storage, data.generatedImagePath));
          } catch (e) {
            console.warn("Failed to load generated image:", e);
          }
        }

        // Only load original image AFTER generated image is ready (for comparison view)
        // This prevents the original from showing before the generated map
        if (genUrl && data.originalImagePath) {
          try {
            origUrl = await getDownloadURL(ref(storage, data.originalImagePath));
          } catch (e) {
            console.warn("Failed to load original image:", e);
          }
        }

        // Watopia dual-map render-only generations carry a secondary 2D map
        let gen2dUrl = null;
        if (genUrl && data.generatedImagePath2d) {
          try {
            gen2dUrl = await getDownloadURL(ref(storage, data.generatedImagePath2d));
          } catch (e) {
            console.warn("Failed to load secondary 2D image:", e);
          }
        }

        // Load transition video if the backend has rendered it
        if (genUrl && data.video?.path) {
          try {
            videoUrl = await getDownloadURL(ref(storage, data.video.path));
          } catch (e) {
            console.warn("Failed to load transition video:", e);
          }
        }
        
        // Only show the post when:
        // 1. Generation is still in progress (ManagePostCard will show the loader with empty images), OR
        // 2. Generation is complete AND we have the generated image
        // CRITICAL: Never show the post with only the original image - always wait for generated image
        if (isGenerating) {
          // Generation in progress - set post with empty images so loader shows
          const postObj = {
            id: postId,
            userId: data.userId,
            user: {
              id: data.userId,
              username: 'you',
              displayName: user?.displayName || 'You',
              avatar: user?.photoURL || '/default-avatar.png'
            },
            images: [], // Empty images = loader will show
            caption: data.stravaStats?.activityName || '',
            activityType: data.stravaStats?.activityType?.toLowerCase() || '',
            stravaActivityId: data.StravaId,
            likes: 0,
            createdAt: data.createdAt?.toDate() || new Date(),
            location: [data.stravaStats?.city, data.stravaStats?.state, data.stravaStats?.country]
              .filter(Boolean)
              .join(', ') || null,
            isLiked: false,
            style: data.promptTemplateId || 'Custom',
            badges: [],
            mapOrientation: data.mapOrientation || 'landscape'
          };

          setPost(postObj);
        } else if (isComplete && genUrl) {
          // Generation complete AND we have generated image - now safe to show
          const mediaItems = [
            { kind: 'image', uri: genUrl, role: 'generated' },
            ...(gen2dUrl ? [{ kind: 'image', uri: gen2dUrl, role: 'generated-2d' }] : []),
            ...(videoUrl ? [{ kind: 'video', uri: videoUrl, role: 'video', orientation: data.video?.orientation }] : []),
            ...(origUrl ? [{ kind: 'image', uri: origUrl, role: 'original' }] : []),
          ];
          const imageEntries = mediaItems.filter((item) => item.kind === 'image');
          const images = imageEntries.map((item) => item.uri);
          const imageTypes = imageEntries.map((item) => item.role);

          const postObj = {
            id: postId,
            userId: data.userId,
            user: {
              id: data.userId,
              username: 'you',
              displayName: user?.displayName || 'You',
              avatar: user?.photoURL || '/default-avatar.png'
            },
            images, // Generated image is always first; preserved for legacy share/download paths
            media: mediaItems,
            caption: data.stravaStats?.activityName || '',
            activityType: data.stravaStats?.activityType?.toLowerCase() || 'ride',
            stravaActivityId: data.StravaId,
            likes: 0,
            createdAt: data.createdAt?.toDate() || new Date(),
            location: [data.stravaStats?.city, data.stravaStats?.state, data.stravaStats?.country]
              .filter(Boolean)
              .join(', ') || null,
            isLiked: false,
            style: data.promptTemplateId || 'Custom',
            badges: [],
            mapOrientation: data.mapOrientation || 'landscape',
            imageTypes
          };

          setPost(postObj);
        }
        // If generation is complete but no generated image yet, don't set post (loader will show via requestData)
        
        // Stop loading state - ManagePostCard will handle showing the loader if generation is in progress
        setIsLoading(false);
      } else {
        setRequestData(null);
        setPost(null);
        setIsLoading(false);
      }
    });
    
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [postId, user, navigate]);

  // Schedule the auto-upload discovery popup 8s after a map becomes ready, so
  // the user has time to enjoy the generated map first. When the timer fires
  // we flip auto-upload on by default; the popup lets the user opt out.
  useEffect(() => {
    if (!user?.uid) return;
    if (autoUploadDiscoveryCheckedRef.current) return;
    if (!requestData) return;
    const isComplete = requestData.status === 'openai_processing_complete' && !requestData.error;
    if (!isComplete) return;
    if (requestData.userId !== user.uid) return;
    autoUploadDiscoveryCheckedRef.current = true;
    let cancelled = false;
    (async () => {
      try {
        const userSnap = await getDoc(doc(db, 'users', user.uid));
        const data = userSnap.data() || {};
        const alreadySeen = data.settings?.autoUploadDiscoverySeen === true;
        // This is the art-map discovery surface, so track the art toggle (with the
        // legacy fallback) rather than the indoor master.
        const auto = data.settings?.autoUpload || {};
        const alreadyEnabled = typeof auto.artEnabled === 'boolean'
          ? auto.artEnabled
          : (auto.enabled === true && auto.zwiftOnly !== true);
        setAutoUploadEnabled(alreadyEnabled);
        if (alreadySeen || alreadyEnabled) return;
        if (cancelled) return;
        autoUploadDiscoveryTimerRef.current = setTimeout(async () => {
          autoUploadDiscoveryTimerRef.current = null;
          try {
            const latestSnap = await getDoc(doc(db, 'users', user.uid));
            const latestData = latestSnap.data() || {};
            const latestAlreadySeen = latestData.settings?.autoUploadDiscoverySeen === true;
            const latestAuto = latestData.settings?.autoUpload || {};
            const latestAlreadyEnabled = typeof latestAuto.artEnabled === 'boolean'
              ? latestAuto.artEnabled
              : (latestAuto.enabled === true && latestAuto.zwiftOnly !== true);
            setAutoUploadEnabled(latestAlreadyEnabled);
            if (cancelled || latestAlreadySeen || latestAlreadyEnabled) {
              return;
            }

            await updateDoc(doc(db, 'users', user.uid), {
              'settings.autoUpload.artEnabled': true,
              'settings.autoUploadDiscoverySeen': true,
            });
            setAutoUploadEnabled(true);
            setShowAutoUploadDiscovery(true);
          } catch (error) {
            console.warn('Failed to enable auto-upload by default:', error);
          }
        }, 8000);
      } catch (error) {
        console.warn('Could not load auto-upload discovery flag:', error);
      }
    })();
    return () => {
      cancelled = true;
      if (autoUploadDiscoveryTimerRef.current) {
        clearTimeout(autoUploadDiscoveryTimerRef.current);
        autoUploadDiscoveryTimerRef.current = null;
      }
    };
  }, [user?.uid, requestData]);

  const dismissAutoUploadDiscovery = () => {
    setShowAutoUploadDiscovery(false);
  };

  const setAutoUploadEnabledFromDiscovery = async (nextEnabled) => {
    const previousEnabled = autoUploadEnabled;
    setAutoUploadEnabled(nextEnabled);
    if (!user?.uid) return;
    try {
      await updateDoc(doc(db, 'users', user.uid), {
        'settings.autoUpload.artEnabled': nextEnabled,
      });
    } catch (error) {
      console.warn('Failed to update auto-upload enabled:', error);
      setAutoUploadEnabled(previousEnabled);
      alert('Could not update auto-upload. Please try from settings.');
    }
  };

  // Refresh credits when generation completes (backend updates credits at this point)
  // Only depend on status, not the entire requestData object, to avoid triggering on every document update
  useEffect(() => {
    if (!user || !onCreditsUpdated || !requestData || hasRefreshedCredits) return;
    
    const currentStatus = requestData.status;
    const previousStatus = previousStatusRef.current;
    
    // Only refresh when status transitions TO 'openai_processing_complete' (not on every update)
    if (currentStatus === 'openai_processing_complete' && 
        previousStatus !== 'openai_processing_complete' && 
        !requestData.error) {
      // Small delay to ensure backend has finished updating credits
      const timer = setTimeout(() => {
        onCreditsUpdated();
        setHasRefreshedCredits(true);
      }, 1500);
      
      previousStatusRef.current = currentStatus;
      return () => clearTimeout(timer);
    }
    
    // Update the ref to track status changes
    previousStatusRef.current = currentStatus;
  }, [requestData?.status, user, onCreditsUpdated, requestData, hasRefreshedCredits]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleDelete = async () => {
    if (!postId || !user) return;
    
    setIsDeleting(true);
    try {
      const deleteMapFunc = httpsCallable(functions, 'deleteMap');
      await deleteMapFunc({ requestId: postId, reason: "User deleted from manage post page" });

      // Track map deletion
      posthog?.capture('history_map_deleted', {
        request_id: postId,
        activity_name: post?.caption || requestData?.name || 'Unknown'
      });

      setShowDeleteModal(false);
      if (user?.uid) {
        navigate(`/profile/${user.uid}`);
      }
    } catch (error) {
      console.error("Error deleting post:", error);
      alert("Failed to delete post. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="manage-post-page">
        <div className="manage-post-loading">
          <div className="spinner"></div>
          <p>{t('managePostPage.loading', 'Loading...')}</p>
        </div>
      </div>
    );
  }

  if (!requestData) {
    return (
      <div className="manage-post-page">
        <div className="manage-post-not-found">
          <h2>{t('managePostPage.postNotFound', 'Post not found')}</h2>
          <p>{t('managePostPage.postNotFoundDesc', "This post may have been deleted or doesn't exist.")}</p>
          <button onClick={() => user?.uid ? navigate(`/profile/${user.uid}`) : navigate('/')}>{t('managePostPage.goToProfile', 'Go to Profile')}</button>
        </div>
      </div>
    );
  }

  if (!isOwnPost) {
    // Redirect to public view
    navigate(`/post/${postId}`);
    return null;
  }

  return (
    <div className="manage-post-page">
      <div className="manage-post-header">
        <button className="back-button" onClick={handleBack}>
          <IconArrowLeft size={20} />
          <span>{t('managePostPage.back', 'Back')}</span>
        </button>
      </div>

      <div className="manage-post-content">
        {post && (
          <div className={`manage-post-card-wrapper ${post.mapOrientation === 'portrait' ? 'portrait-map' : 'landscape-map'}`}>
            <ManagePostCard
              post={post}
              user={user}
              requestData={effectiveRequestData}
              onDelete={() => setShowDeleteModal(true)}
              onCreditsAwarded={() => onCreditsUpdated?.()}
              onAutoUploadEnabledChange={setAutoUploadEnabled}
            />
          </div>
        )}

        {autoUploadEnabled && (
          <Link to="/settings#auto-upload" className="manage-post-autoupload-link">
            Manage auto-upload settings
          </Link>
        )}

        <div className="manage-post-feedback">
          <p>{t('managePostPage.feedbackPrompt', 'Thoughts? Feedback? We\'d love to hear from you')}</p>
          <div className="manage-post-feedback-links">
            <a href="https://discord.gg/neXEXTcQpW" target="_blank" rel="noopener noreferrer" title="Discord">
              <svg width="20" height="20" viewBox="0 0 127.14 96.36" fill="currentColor">
                <path d="M107.7,8.07A105.15,105.15,0,0,0,81.47,0a72.06,72.06,0,0,0-3.36,6.83A97.68,97.68,0,0,0,49,6.83,72.37,72.37,0,0,0,45.64,0,105.89,105.89,0,0,0,19.39,8.09C2.79,32.65-1.71,56.6.54,80.21h0A105.73,105.73,0,0,0,32.71,96.36,77.7,77.7,0,0,0,39.6,85.25a68.42,68.42,0,0,1-10.85-5.18c.91-.66,1.8-1.34,2.66-2a75.57,75.57,0,0,0,64.32,0c.87.71,1.76,1.39,2.66,2a68.68,68.68,0,0,1-10.87,5.19,77,77,0,0,0,6.89,11.1A105.25,105.25,0,0,0,126.6,80.22h0C129.24,52.84,122.09,29.11,107.7,8.07ZM42.45,65.69C36.18,65.69,31,60,31,53s5-12.74,11.43-12.74S54,46,53.89,53,48.84,65.69,42.45,65.69Zm42.24,0C78.41,65.69,73.25,60,73.25,53s5-12.74,11.44-12.74S96.23,46,96.12,53,91.08,65.69,84.69,65.69Z"/>
              </svg>
            </a>
            <a href="https://www.reddit.com/r/mythicmapsai/" target="_blank" rel="noopener noreferrer" title="Reddit">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 0C5.373 0 0 5.373 0 12c0 3.314 1.343 6.314 3.515 8.485l-2.286 2.286C.775 23.225 1.097 24 1.738 24H12c6.627 0 12-5.373 12-12S18.627 0 12 0Zm4.388 3.199c1.104 0 1.999.895 1.999 1.999 0 1.105-.895 2-1.999 2-.946 0-1.739-.657-1.947-1.539v.002c-1.147.162-2.032 1.15-2.032 2.341v.007c1.776.067 3.4.567 4.686 1.363.473-.363 1.064-.58 1.707-.58 1.547 0 2.802 1.254 2.802 2.802 0 1.117-.655 2.081-1.601 2.531-.088 3.256-3.637 5.876-7.997 5.876-4.361 0-7.905-2.617-7.998-5.87-.954-.447-1.614-1.415-1.614-2.538 0-1.548 1.255-2.802 2.803-2.802.645 0 1.239.218 1.712.585 1.275-.79 2.881-1.291 4.64-1.365v-.01c0-1.663 1.263-3.034 2.88-3.207.188-.911.993-1.595 1.959-1.595Zm-8.085 8.376c-.784 0-1.459.78-1.506 1.797-.047 1.016.64 1.429 1.426 1.429.786 0 1.371-.369 1.418-1.385.047-1.017-.553-1.841-1.338-1.841Zm7.406 0c-.786 0-1.385.824-1.338 1.841.047 1.017.634 1.385 1.418 1.385.785 0 1.473-.413 1.426-1.429-.046-1.017-.721-1.797-1.506-1.797Zm-3.703 4.013c-.974 0-1.907.048-2.77.135-.147.015-.241.168-.183.305.483 1.154 1.622 1.964 2.953 1.964 1.33 0 2.47-.81 2.953-1.964.057-.137-.037-.29-.184-.305-.863-.087-1.795-.135-2.769-.135Z"/>
              </svg>
            </a>
            <a href="https://www.strava.com/clubs/mythicmaps" target="_blank" rel="noopener noreferrer" title="Strava Club">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M15.387 17.944l-2.089-4.116h-3.065L15.387 24l5.15-10.172h-3.066m-7.008-5.599l2.836 5.598h4.172L10.463 0l-7 13.828h4.169"/>
              </svg>
            </a>
            <a href="mailto:contact@mythicmaps.ai" title="Email">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="4" width="20" height="16" rx="2"/>
                <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
              </svg>
            </a>
          </div>
        </div>
      </div>

      {showAutoUploadDiscovery && (
        <AutoUploadDiscoveryModal
          enabled={autoUploadEnabled}
          onSetEnabled={setAutoUploadEnabledFromDiscovery}
          onDismiss={dismissAutoUploadDiscovery}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>{t('managePostPage.deletePost', 'Delete Post?')}</h3>
            <p>{t('managePostPage.deletePostDesc', 'This will permanently delete your map. This action cannot be undone.')}</p>
            <div className="modal-actions">
              <button onClick={() => setShowDeleteModal(false)} className="cancel-button" disabled={isDeleting}>
                {t('managePostPage.cancel', 'Cancel')}
              </button>
              <button onClick={handleDelete} className="delete-confirm-button" disabled={isDeleting}>
                {isDeleting ? t('managePostPage.deleting', 'Deleting...') : t('managePostPage.delete', 'Delete')}
              </button>
            </div>
          </div>
        </div>
      )}
      <Footer />
    </div>
  );
};

export default ManagePostPage;
